import { tsoPqTheme } from '@assets/style/customTsoPqtheme';
import { StyledLabel, StyledMandatory } from '@components/Datepicker/DatepickerTsoPq';
import { faBan, faCheck, faHourglassHalf } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
  Autocomplete,
  Box,
  List,
  ListItem,
  ListItemIcon,
  Paper,
  styled,
  TextField,
  Typography,
  TypographyProps,
} from '@mui/material';
import { ClearIcon } from '@mui/x-date-pickers';
import { forwardRef, useEffect, useState } from 'react';

type SelectTsoPqProps = {
  options: Option[];
  supportingText?: string;
  error?: boolean;
  customwidth?: string;
  mandatory?: boolean;
  label: string;
  multipleValues?: boolean;
  disabled?: boolean;
  reset?: boolean;
  value: Option | null | undefined;
  defaultValue?: string;
  placeholder?: string;
  register?: any;
  watch?: any;
  name?: string;
  setValue?: any;
  limitTags?: number;
  clearable?: boolean;
  handleChange?: (value: Option | null) => void;
};

export type Option = {
  id: number;
  value?: string;
  label: string;
  disabled?: boolean;
  waiting?: boolean;
};

type StyledSupportingTextProps = TypographyProps & {
  error?: boolean;
};

const StyledPaper = styled(Paper)(() => {
  return {
    padding: '0.625rem 0.625rem 0.625rem 0',
  };
});

const StyledAutocomplete = styled(Autocomplete, {
  shouldForwardProp: (prop) => !['width', 'setValue'].includes(prop as string),
})(({ width }: { width: string | undefined }) => {
  return {
    '& .MuiAutocomplete-root': {
      width: width ?? 'auto',
    },

    '& .MuiInputBase-root': {
      width: width ?? 'auto',
      padding: `0px ${tsoPqTheme.spacingValues.componentSpacing.spacing_component_gap_4}`,

      '& .MuiAutocomplete-endAdornment': {
        '& .MuiIconButton-root': {
          '&:hover': {
            background: 'transparent',
          },
          '&:active': {
            background: 'transparent',
          },
          '&:focus': {
            background: 'transparent',
          },
        },
      },
    },
    '& .MuiChip-root': {
      margin: '0.25rem',
      height: '1.625rem',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: tsoPqTheme.spacingValues.componentSpacing.spacing_component_gap_4,
      padding: `${tsoPqTheme.spacingValues.componentSpacing.spacing_component_gap_4} ${tsoPqTheme.spacingValues.atomSpacing.spacing_atom_gap_com_gap_m}`,
      borderRadius: tsoPqTheme.shape.border_radius_tso_border_radius_4,
      background: tsoPqTheme.palette.background.primary.color_primary_background_bg_prim_accent,
      color: tsoPqTheme.palette.customTextColor.color_neutral_font_ft_nrtrl_enabled,

      '& .MuiChip-label': {
        padding: '0',
      },

      '& svg': {
        padding: '0',
        margin: '0',
        background: 'transparent',
      },
    },
  };
});

const StyledTextField = styled(TextField, {
  shouldForwardProp: (prop) => prop !== 'width',
})(({ width, error }: { width: string | undefined; error: boolean }) => {
  return {
    flex: 1,
    background: tsoPqTheme.palette.background.other.color_other_background_white,
    '& .MuiInputBase-root': {
      width: width ?? '13.25rem',
      fontSize: tsoPqTheme.typography.fontsize_body_body_medium,
      fontFamily: tsoPqTheme.typography.openSans,
      borderColor: error && tsoPqTheme.palette.customError.color_signal_error_error,

      '&:hover': {
        background: tsoPqTheme.palette.background.neutral.color_neutral_background_bg_ntrl_enabled_lt,
      },

      '&.Mui-focused': {
        background: tsoPqTheme.palette.background.primary.color_primary_background_bg_prim_lt,
      },

      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: tsoPqTheme.palette.stroke.color_primary_stroke_str_prim_hover,
      },

      '& .MuiInputBase-input': {
        padding: '0.5rem 0 0.5rem 0.25rem',
      },

      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        border: `1px solid ${tsoPqTheme.palette.stroke.color_primary_stroke_str_prim_hover}`,
      },

      '&.Mui-error .MuiOutlinedInput-notchedOutline': {
        border: `1px solid ${tsoPqTheme.palette.customError.color_signal_error_error}`,
      },

      '&.Mui-disabled': {
        backgroundColor: tsoPqTheme.palette.background.neutral.color_neutral_background_bg_ntrl_disabled,

        '& .MuiInputBase-input': {
          WebkitTextFillColor: tsoPqTheme.palette.customTextColor.color_font_ft_nrtrl_disabled,
        },
      },
    },
  };
});

const StyledListbox = styled(List)(() => {
  return {
    maxHeight: '16.75rem',
    '&::-webkit-scrollbar': {
      width: '3px',
    },
    '&::-webkit-scrollbar-thumb': {
      background: '#555c62',
      borderRadius: '4px',
    },
    '&::-webkit-scrollbar-track': {
      background: '#c3c7ca',
      borderRadius: '4px',
    },
  };
});

const StyledListItem = styled(ListItem)<{ selected: any }>((selected) => {
  return {
    background:
      selected.selected && `${tsoPqTheme.palette.background.primary.color_primary_background_bg_prim} !important`,
    display: 'flex',
    gap: tsoPqTheme.spacingValues.atomSpacing.spacing_atom_gap_com_gap_m,

    '&.MuiListItem-root': {
      color: tsoPqTheme.palette.customTextColor.color_font_ft_text,
      fontFamily: tsoPqTheme.typography.openSans,
      fontSize: tsoPqTheme.typography.fontsize_body_body_medium,
      fontStyle: 'normal',
      fontWeight: 400,
      lineHeight: '1.25rem',
    },

    '& .MuiListItemIcon-root': {
      minWidth: '0.875rem',
    },

    '&:hover': {
      background: `${tsoPqTheme.palette.background.neutral.color_neutral_background_bg_ntrl_accent} !important`,
    },
  };
});

const StyledListItemIcon = styled(ListItemIcon, {
  shouldForwardProp: (prop) => prop !== 'active',
})(({ active = false }: { active: boolean }) => {
  return {
    width: '2px',
    '& svg': {
      width: '0.875rem',
      color: active
        ? tsoPqTheme.palette.customTextColor.color_primary_font_ft_prim_accent
        : tsoPqTheme.palette.customTextColor.color_font_ft_nrtrl_disabled,
    },
  };
});

const StyledSelectContainer = styled(Box)(() => {
  return {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.375rem',
    width: 'fit-content',
  };
});

const StyledClearIcon = styled(ClearIcon)(() => {
  return {
    color: tsoPqTheme.palette.customTextColor.color_primary_font_ft_prim_accent,
    padding: tsoPqTheme.spacingValues.componentSpacing.spacing_component_padding_4,
    height: '1.125rem',
    '&:hover': {
      cursor: 'pointer',
    },

    '&svg': {
      margin: 0,
    },
  };
});

const StyledSupportingText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'error',
})<StyledSupportingTextProps>(({ error }) => {
  return {
    color: error
      ? tsoPqTheme.palette.customError.color_signal_error_error
      : tsoPqTheme.palette.customTextColor.color_font_ft_helping_text,
    fontFamily: tsoPqTheme.typography.openSans,
    fontSize: tsoPqTheme.typography.fontsize_body_body_small,
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '0.875rem',
    padding: '0 0 0 0.5rem',
  };
});

const SelectTsoPq = forwardRef(
  (
    {
      options,
      supportingText,
      error = false,
      customwidth,
      mandatory,
      label,
      multipleValues = false,
      disabled = false,
      defaultValue,
      value,
      placeholder,
      watch,
      name,
      clearable,
      reset,
      setValue,
      handleChange,
    }: SelectTsoPqProps,
    ref
  ) => {
    const selectedId = watch?.(name);
    const [selectedOption, setSelectedOption] = useState<Option | null>(null);

    const handleOptionsChange = (value: Option | null) => {
      setValue?.(name, value?.id ?? null);
      handleChange?.(value);
      setSelectedOption(value);
    };

    useEffect(() => {
      if (options.length > 0 && selectedId !== '' && selectedId !== null && selectedId !== undefined) {
        const found = options.find((option) => option.id === Number(selectedId));
        if (found && found !== selectedOption) {
          setSelectedOption(found);
        }
      }
    }, [selectedId, options]);

    useEffect(() => {
      if (reset) setSelectedOption(null);
    }, [reset]);

    useEffect(() => {
      setSelectedOption((previous) => {
        const next = value ?? null;

        if (previous?.id === next?.id) {
          return previous;
        }

        return next;
      });
    }, [value]);

    return (
      <StyledSelectContainer>
        <StyledLabel>
          {label}
          {mandatory && <StyledMandatory>*</StyledMandatory>}
        </StyledLabel>
        <StyledAutocomplete
          value={selectedOption}
          onChange={(_, value: Option | null) => handleOptionsChange(value)}
          width={customwidth}
          multiple={multipleValues}
          disabled={disabled}
          defaultValue={defaultValue}
          options={options}
          disableClearable={!clearable}
          limitTags={1}
          getOptionLabel={(option: Option) => option?.label ?? ''}
          getOptionDisabled={(option: Option) => option.disabled || option.waiting}
          isOptionEqualToValue={(option: Option, value: Option) => option.id === value.id}
          renderInput={(params) => (
            <StyledTextField {...params} error={error} width={customwidth} placeholder={placeholder} inputRef={ref} />
          )}
          renderOption={({ key, ...rest }: { key: string }, option: Option, { selected }: { selected: boolean }) => (
            <StyledListItem key={key} {...rest} selected={selected}>
              <StyledListItemIcon active={selected}>
                {selected && <FontAwesomeIcon icon={faCheck} />}
                {option.disabled && <FontAwesomeIcon icon={faBan} />}
                {option.waiting && <FontAwesomeIcon icon={faHourglassHalf} />}
              </StyledListItemIcon>
              {(option as Option).label}
            </StyledListItem>
          )}
          slotProps={{
            paper: { component: StyledPaper },
            chip: { deleteIcon: <StyledClearIcon /> },
            listbox: { component: StyledListbox },
          }}
          popupIcon={<KeyboardArrowDownIcon />}
          openOnFocus
        />

        {supportingText && <StyledSupportingText error={error}>{supportingText}</StyledSupportingText>}
      </StyledSelectContainer>
    );
  }
);

export default SelectTsoPq;
